import {
  Attribute,
  AttributeLocalizedEnumValue,
  AttributePlainEnumValue,
  CategoryReference,
  CustomObjectReference,
  LocalizedString,
  Money,
  ProductReference,
  Reference,
} from '@commercetools/platform-sdk';
import type { OverrideProperties } from 'type-fest';

/**
 * The keys that are available for the "customProduct" attribute found in the Nuts.com product type.
 *
 * @see https://github.com/nuts/webstore/blob/main/replatforming/configuration/data/product-types/nuts.com.json
 */
export type CustomProductKey =
  | 'Custom Mix'
  | 'Custom Tray'
  | 'Dynamic Bundle'
  | 'Gift Certificate'
  | 'Greeting Card';

type LocalizedEnumValueOfKeys<K extends string> = OverrideProperties<
  AttributeLocalizedEnumValue,
  { readonly key: K }
>;

type PlainEnumValueOfKeys<K extends string> = OverrideProperties<
  AttributePlainEnumValue,
  { readonly key: K }
>;

/**
 * Product type `Nuts.com Product` core attributes (`"attributeConstraint": "SameForAll"`)
 *
 * Must be kept in sync with the raw JSON schema:
 *
 * @see https://github.com/nuts/webstore/blob/main/replatforming/configuration/data/product-types/nuts.com.json
 */
export interface NutsProductAttributes {
  allergens?: LocalizedString;
  /** Content list for Gifting products; can contain HTML code for text formatting. */
  contentList?: LocalizedString;
  countPerPound?: number;
  /** Country code; e.g. "US", "CA", etc. */
  countryOfOrigin?: AttributeLocalizedEnumValue;
  customPageTitle?: LocalizedString;
  customProduct?: PlainEnumValueOfKeys<CustomProductKey>;
  density?: number;
  googleProductCategory?: AttributePlainEnumValue;
  healthTips?: LocalizedString;
  hiddenFromDy: boolean;
  hiddenFromSearch: boolean;
  hsCode?: string;
  inMix: boolean;
  inTray: boolean;
  ingredients?: LocalizedString;
  ingredientsDisclaimer?: LocalizedString;
  instructions?: LocalizedString;
  meltable: PlainEnumValueOfKeys<'yes' | 'yes-only-wholesale' | 'no'>;
  merchandisingCategory?: PlainEnumValueOfKeys<
    | 'Baking'
    | 'Chocolates & Sweets'
    | 'Coffees & Teas'
    | 'Cooking'
    | 'Dried Fruit'
    | 'Gifts'
    | 'Nuts'
    | 'Powders'
    | 'Seeds'
    | 'Snacks'
    | 'Vitamins'
  >;
  nutritionFacts?: Reference;
  /** Offer gift add-ons on the product detail page */
  offerGiftAddOns?: boolean;
  /** Describe the product's taste with 3-4 keywords (e.g Smoky, sweet, buttery) */
  oneLinerDescription?: LocalizedString;
  /** Between 0 and 1. Default = 0.5, which is like DisplayPrecedence = 0 */
  orderHint?: number;
  plaDescription?: LocalizedString;
  plaEnabled: boolean;
  plaTitle?: LocalizedString;
  popularity?: number;
  primaryCategory: CategoryReference;
  remarketingEnabled: boolean;
  reportingCategory?: AttributePlainEnumValue;
  requiresCustomization: boolean;
  /** When saved, applies to all active variants and then clears itself */
  setBackordered?: PlainEnumValueOfKeys<
    'yes' | 'yes-pause-advertising' | 'no' | 'no-notify' | 'no-notify-resume-advertising'
  >;
  /** When saved, applies to all active variants and then clears itself; ISO date string */
  setBackorderedUntil?: string;
  shortDescription?: LocalizedString;
  storageRequirements?: AttributeLocalizedEnumValue;
  tagReferences?: CustomObjectReference[];
  /** Alternate product description for A/B testing content changes; can contain HTML code for text formatting. */
  testDescription?: LocalizedString;
  upsellingProducts?: ProductReference[];
  urlName: string;
  wholesalePopularity?: number;
}

/**
 * Product type `Nuts.com Product` variant attributes (`"attributeConstraint": "None"`)
 *
 * Must be kept in sync with the raw JSON schema:
 *
 * @see https://github.com/nuts/webstore/blob/main/replatforming/configuration/data/product-types/nuts.com.json
 */
export interface NutsProductVariantAttributes {
  active: boolean;
  /** Auto-backorder based on NJ piece counts */
  autoBackorder: boolean;
  autoDeliveryEligible: boolean;
  /** If YES, the variant will be automatically put back in stock when available */
  autoInStock: boolean;
  automaticPriceTiers: boolean;
  backordered: PlainEnumValueOfKeys<
    'yes' | 'yes-pause-advertising' | 'no' | 'no-notify' | 'no-notify-resume-advertising'
  >;
  /** ISO date string */
  backorderedUntil?: string;
  bagSize?: PlainEnumValueOfKeys<
    | 'NSSB'
    | 'NSS'
    | 'NSM'
    | 'NSLB'
    | 'CSSB'
    | 'CSS'
    | 'CSL'
    | 'SNS'
    | 'SNL'
    | 'SNLB'
    | 'DFSB'
    | 'DFS'
    | 'DFL'
    | 'DFLB'
    | '1 Pound Bag - Small'
    | '1 Pound Bag - Medium'
    | '1 Pound Bag - Large'
    | '5 Pound Bag - White'
    | '5 Pound Bag - Brown'
    | 'Bubble Wrap'
    | 'Small Black Bag'
    | 'Large Black Bag'
    | '16 Ounce Tamper Evident Container'
    | '24 Ounce Square Container'
    | '24 Ounce Flat Round Container'
    | '48 Ounce Round Container'
    | 'Medium Unmarked Bag'
    | 'Medium Valve Bag'
    | 'Sample Bag'
    | '05 lb. box'
    | '07 lb. box'
    | '30 lb. box'
    | '38 lb. box'
    | '44 lb. box'
    | 'Durey Libby box'
    | 'Date box'
    | 'Oversized marshmallow box'
  >;
  bulk: boolean;
  caffeine?: LocalizedEnumValueOfKeys<'Regular' | 'Decaffeinated'>;
  /** If set, one or more of the following: PDP, BYOB, CSB, FSP (CSB: Custom Snack Box, FSP: Free Sample Program) */
  customAvailability?: string;
  /** SKU to replace this one in AD. Triggers if variant is deactivated. Must be same Product. Optionally prefix with quantity, e.g. '2x4003-01' */
  discontinuedReplacementSku?: string;
  effectiveVolume?: number;
  /** Optional JSON string */
  fixedAddress?: string;
  fulfillmentException?: PlainEnumValueOfKeys<'coffee_ward'>;
  grindSize?: LocalizedEnumValueOfKeys<
    | 'Whole Bean'
    | 'Ground Perc'
    | 'Ground Auto Drip'
    | 'Ground Fine'
    | 'Ground Espresso'
    | 'Turkish'
  >;
  /** ISO date string */
  introducedOn?: string;
  marginTarget?: number;
  /** ISO date string */
  marginWarningsIgnoredUntil?: string;
  maximumPiecesPerOrder?: number;
  ordergrooveSkuSwapGroup?: string;
  pieceCost?: Money;
  sample: boolean;
  servingsPerContainer?: number;
  snackPack: boolean;
  tinColor?: LocalizedEnumValueOfKeys<
    | 'Gold Tin'
    | 'Party Tin with Balloons'
    | 'Platinum Tin'
    | "Valentine's Tin"
    | 'Holiday Tin'
    | 'Heart Tin'
  >;
  variantLabel?: PlainEnumValueOfKeys<
    'Small' | 'Medium' | 'Large' | 'X-Large' | 'Bulk' | 'Single-Serve Pack' | 'Sample'
  >;
  /** Like our old sku.Name; describe packaging, in plural form. ex: "1 Pound Bags", "Boxes of 100 Snack Pack Bags" */
  variantName?: LocalizedString;
  weight: number;
  wholesale: boolean;
}

/**
 * Type-aware attribute lookup
 *
 * If the attribute is not present in either the product or variant definitions, throws a type error
 * and returns undefined.
 */
export function findAttributeByName<
  K extends keyof NutsProductAttributes | keyof NutsProductVariantAttributes,
>(
  attributes: Attribute[] | undefined,
  name: K,
):
  | (K extends keyof NutsProductAttributes
      ? NutsProductAttributes[K]
      : K extends keyof NutsProductVariantAttributes
      ? NutsProductVariantAttributes[K]
      : never)
  | undefined {
  const attribute = attributes?.find((a) => a.name === name);
  if (!attribute) return undefined;
  return attribute.value;
}
